import {
    createRouter,
    createWebHistory,
    RouteLocationNormalized,
    RouteLocationNormalizedLoaded,
} from "vue-router";
import { globalEvents } from "@/event-bus";
import { $tr } from "@/i18n";
import { useAccountStore } from "./model/account";
import { useDevicePreferencesStore } from "./store/device-preferences-store";
import { useDeckStore } from "@/store/deck-store";
import { openSignInModal } from "@/composables/modal";
import { useTileStore } from "@/store/tile-store";

export const routes = [
    {
        path: "/@:acct/following",
        component: () => import("./pages/user/following.vue"),
        props: (route) => ({ acct: route.params.acct }),
    },
    {
        path: "/@:acct/followers",
        component: () => import("./pages/user/followers.vue"),
        props: (route) => ({ acct: route.params.acct }),
    },
    {
        name: "user",
        path: "/@:acct/:page?",
        component: () => import("./pages/user/index.vue"),
    },
    {
        name: "note",
        path: "/notes/:noteId",
        component: () => import("./pages/note.vue"),
        props: (route: RouteLocationNormalized) => ({
            noteId: route.params.noteId,
        }),
    },
    {
        path: "/clips/:clipId",
        component: () => import("./pages/clip.vue"),
        props: (route: RouteLocationNormalized) => ({
            clipId: route.params.clipId,
        }),
    },
    {
        path: "/user-info/:userId",
        component: () => import("./pages/user-info.vue"),
        props: (route: RouteLocationNormalized) => ({
            userId: route.params.userId,
        }),
    },
    {
        path: "/instance-info/:host",
        component: () => import("./pages/instance-info.vue"),
        props: (route: RouteLocationNormalized) => ({
            host: route.params.host,
        }),
    },
    {
        name: "settings",
        path: "/settings",
        component: () => import("./pages/settings/index.vue"),
        props: (route: RouteLocationNormalized) => ({
            allowSidebar: route.query.allowSidebar !== "false",
        }),
        meta: {
            auth: "normal",
        },
        children: [
            {
                path: "profile",
                name: "settings/profile",
                component: () => import("./pages/settings/profile.vue"),
            },
            {
                path: "privacy",
                name: "settings/privacy",
                component: () => import("./pages/settings/privacy.vue"),
            },
            {
                path: "reaction",
                name: "settings/reaction",
                component: () => import("./pages/settings/reaction.vue"),
            },
            {
                path: "drive",
                name: "settings/drive",
                component: () => import("./pages/settings/drive.vue"),
            },
            {
                path: "notifications",
                name: "settings/notifications",
                component: () => import("./pages/settings/notifications.vue"),
            },
            {
                path: "email",
                name: "settings/email",
                component: () => import("./pages/settings/email.vue"),
            },
            {
                path: "security",
                name: "settings/security",
                component: () => import("./pages/settings/security.vue"),
            },
            {
                path: "general",
                name: "settings/general",
                component: () => import("./pages/settings/general.vue"),
            },
            {
                path: "accessibility",
                name: "settings/accessibility",
                component: () => import("./pages/settings/accessibility.vue"),
                meta: {
                    auth: null,
                },
            },
            {
                path: "theme/install",
                name: "settings/theme/install",
                component: () => import("./pages/settings/theme.install.vue"),
            },
            {
                path: "theme/manage",
                name: "settings/theme/manage",
                component: () => import("./pages/settings/theme.manage.vue"),
            },
            {
                path: "theme",
                name: "settings/theme",
                component: () => import("./pages/settings/theme.vue"),
            },
            {
                path: "custom-css",
                name: "settings/custom-css",
                component: () => import("./pages/settings/custom-css.vue"),
            },
            {
                path: "custom-katex-macro",
                name: "settings/custom-katex-macro",
                component: () =>
                    import("./pages/settings/custom-katex-macro.vue"),
            },
            {
                path: "account-info",
                name: "settings/account-info",
                component: () => import("./pages/settings/account-info.vue"),
            },
            {
                path: "navbar",
                name: "settings/navbar",
                component: () => import("./pages/settings/navbar.vue"),
            },
            {
                path: "statusbar",
                name: "settings/statusbar",
                component: () => import("./pages/settings/statusbar.vue"),
            },
            {
                path: "sounds",
                name: "settings/sounds",
                component: () => import("./pages/settings/sounds.vue"),
            },
            {
                path: "import-export",
                name: "settings/import-export",
                component: () => import("./pages/settings/import-export.vue"),
            },
            {
                path: "self-moderation/instance",
                name: "settings/self-moderation/instance",
                component: () =>
                    import("./pages/settings/self-moderation/instance.vue"),
            },
            {
                path: "self-moderation/weak",
                name: "settings/self-moderation/weak",
                component: () =>
                    import("./pages/settings/self-moderation/weak.vue"),
            },
            {
                path: "self-moderation/hard",
                name: "settings/self-moderation/hard",
                component: () =>
                    import("./pages/settings/self-moderation/hard.vue"),
            },
            {
                path: "self-moderation/user-blocks",
                name: "settings/self-moderation/user-blocks",
                component: () =>
                    import("./pages/settings/self-moderation/user-blocks.vue"),
            },
            {
                path: "self-moderation/user-mutes",
                name: "settings/self-moderation/user-mutes",
                component: () =>
                    import("./pages/settings/self-moderation/user-mutes.vue"),
            },
            {
                path: "self-moderation/user-advanced",
                name: "settings/self-moderation/user-advanced",
                component: () =>
                    import(
                        "./pages/settings/self-moderation/user-advanced.vue"
                    ),
            },
            {
                path: "self-moderation",
                name: "settings/self-moderation",
                component: () =>
                    import("./pages/settings/self-moderation/index.vue"),
            },
            {
                path: "api",
                name: "settings/api",
                component: () => import("./pages/settings/api.vue"),
            },
            {
                path: "apps",
                name: "settings/apps",
                component: () => import("./pages/settings/apps.vue"),
            },
            {
                path: "webhook/edit/:webhookId",
                name: "settings/webhook/edit",
                props: (route: RouteLocationNormalized) => ({
                    webhookId: route.params.webhookId,
                }),
                component: () => import("./pages/settings/webhook.edit.vue"),
            },
            {
                path: "webhook/new",
                name: "settings/webhook/new",
                component: () => import("./pages/settings/webhook.new.vue"),
            },
            {
                path: "webhook",
                name: "settings/webhook",
                component: () => import("./pages/settings/webhook.vue"),
            },
            {
                path: "deck",
                name: "settings/deck",
                component: () => import("./pages/settings/deck.vue"),
            },
            {
                path: "delete-account",
                name: "settings/delete-account",
                component: () => import("./pages/settings/delete-account.vue"),
            },
            {
                path: "preferences-backups",
                name: "settings/preferences-backups",
                component: () =>
                    import("./pages/settings/preferences-backups.vue"),
            },
            {
                path: "migration",
                name: "settings/migration",
                component: () => import("./pages/settings/migration.vue"),
            },
            {
                path: "custom-css",
                name: "settings/custom-css",
                component: () => import("./pages/settings/custom-css.vue"),
            },
            {
                path: "custom-katex-macro",
                name: "settings/custom-katex-macro",
                component: () =>
                    import("./pages/settings/custom-katex-macro.vue"),
            },
            {
                path: "accounts",
                name: "settings/accounts",
                component: () => import("./pages/settings/accounts.vue"),
            },
            {
                path: "account-info",
                name: "settings/account-info",
                component: () => import("./pages/settings/account-info.vue"),
            },
            {
                path: "delete-account",
                name: "settings/delete-account",
                component: () => import("./pages/settings/delete-account.vue"),
            },
            {
                path: "other",
                name: "settings/other",
                component: () => import("./pages/settings/other.vue"),
            },
        ],
    },
    {
        path: "/reset-password/:token?",
        component: () => import("./pages/reset-password.vue"),
    },
    {
        path: "/signup-complete/:code",
        component: () => import("./pages/signup-complete.vue"),
    },
    {
        path: "/verify-email/:code",
        component: () => import("./pages/verify-email.vue"),
    },
    {
        path: "/announcements",
        component: () => import("./pages/announcements.vue"),
    },
    {
        path: "/about",
        component: () => import("./pages/about.vue"),
        hash: "initialTab",
    },
    {
        path: "/about-magnetar",
        component: () => import("./pages/about-magnetar.vue"),
    },
    {
        path: "/theme-editor",
        component: () => import("./pages/theme-editor.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/explore/tags/:tag",
        component: () => import("./pages/explore.vue"),
    },
    {
        path: "/explore",
        component: () => import("./pages/explore.vue"),
    },
    {
        name: "search",
        path: "/search",
        component: () => import("./pages/search.vue"),
        props: (route: RouteLocationNormalized) => ({
            query: route.query.q,
        }),
    },
    {
        path: "/authorize-follow",
        component: () => import("./pages/follow.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/share",
        component: () => import("./pages/share.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/mfm-cheat-sheet",
        component: () => import("./pages/mfm-cheat-sheet.vue"),
    },
    {
        path: "/auth/:token",
        component: () => import("./pages/auth.vue"),
        props: (route: RouteLocationNormalized) => ({
            token: route.params.token,
        }),
    },
    {
        path: "/miauth/:session",
        component: () => import("./pages/miauth.vue"),
        props: (route: RouteLocationNormalized) => ({
            session: route.params.session,
            permission: route.query.permission,
            icon: route.query.icon,
            name: route.query.name,
            callback: route.query.callback || undefined,
        }),
    },
    {
        path: "/tags/:tag",
        props: (route: RouteLocationNormalized) => ({
            tag: route.params.tag,
        }),
        component: () => import("./pages/tag.vue"),
    },
    {
        path: "/admin/file/:fileId",
        component: () => import("./pages/admin-file.vue"),
        props: (route: RouteLocationNormalized) => ({
            fileId: route.params.fileId,
        }),
        meta: {
            auth: "moderator",
        },
    },
    {
        name: "admin",
        path: "/admin",
        component: () => import("./pages/admin/index.vue"),
        meta: {
            auth: "moderator",
        },
        children: [
            {
                path: "overview",
                name: "admin/overview",
                component: () => import("./pages/admin/overview.vue"),
            },
            {
                path: "users",
                name: "admin/users",
                component: () => import("./pages/admin/users.vue"),
            },
            {
                path: "emojis",
                name: "admin/emojis",
                component: () => import("./pages/admin/emojis.vue"),
            },
            {
                path: "federation",
                name: "admin/federation",
                component: () => import("./pages/admin/federation.vue"),
            },
            {
                path: "queue",
                name: "admin/queue",
                component: () => import("./pages/admin/queue.vue"),
            },
            {
                path: "files",
                name: "admin/files",
                component: () => import("./pages/admin/files.vue"),
            },
            {
                path: "announcements",
                name: "admin/announcements",
                component: () => import("./pages/admin/announcements.vue"),
            },
            {
                path: "database",
                name: "admin/database",
                component: () => import("./pages/admin/database.vue"),
            },
            {
                path: "abuses",
                name: "admin/abuses",
                component: () => import("./pages/admin/abuses.vue"),
            },
            {
                path: "settings",
                name: "admin/settings",
                component: () => import("./pages/admin/settings.vue"),
            },
            {
                path: "email-settings",
                name: "admin/email-settings",
                component: () => import("./pages/admin/email-settings.vue"),
            },
            {
                path: "object-storage",
                name: "admin/object-storage",
                component: () => import("./pages/admin/object-storage.vue"),
            },
            {
                path: "security",
                name: "admin/security",
                component: () => import("./pages/admin/security.vue"),
            },
            {
                path: "relays",
                name: "admin/relays",
                component: () => import("./pages/admin/relays.vue"),
            },
            {
                path: "moderation",
                name: "admin/moderation",
                component: () => import("./pages/admin/moderation.vue"),
            },
            {
                path: "proxy-account",
                name: "admin/proxy-account",
                component: () => import("./pages/admin/proxy-account.vue"),
            },
            {
                path: "other-settings",
                name: "admin/other-settings",
                component: () => import("./pages/admin/other-settings.vue"),
            },
            {
                path: "other-settings",
                name: "admin/other-settings",
                component: () => import("./pages/admin/custom-css.vue"),
            },
            {
                path: "experiments",
                name: "admin/experiments",
                component: () => import("./pages/admin/experiments.vue"),
            },
        ],
    },
    {
        name: "notifications",
        path: "/my/notifications",
        component: () => import("./pages/notifications.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/favorites",
        component: () => import("./pages/favorites.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/drive/folder/:folder",
        component: () => import("./pages/drive.vue"),
        props: (route: RouteLocationNormalized) => ({
            folder: route.params.folder,
        }),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/drive",
        component: () => import("./pages/drive.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/follow-requests",
        component: () => import("./pages/follow-requests.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/lists/:listId",
        component: () => import("./pages/my-lists/list.vue"),
        props: (route: RouteLocationNormalized) => ({
            listId: route.params.listId,
        }),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/lists",
        component: () => import("./pages/my-lists/index.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/clips",
        component: () => import("./pages/my-clips/index.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/antennas/create",
        component: () => import("./pages/my-antennas/create.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/antennas/:antennaId",
        component: () => import("./pages/my-antennas/edit.vue"),
        props: (route: RouteLocationNormalized) => ({
            antennaId: route.params.antennaId,
        }),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/my/antennas",
        component: () => import("./pages/my-antennas/index.vue"),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/timeline/list/:listId",
        component: () => import("./pages/user-list-timeline.vue"),
        props: (route: RouteLocationNormalized) => ({
            listId: route.params.listId,
        }),
        meta: {
            auth: "normal",
        },
    },
    {
        path: "/timeline/antenna/:antennaId",
        component: () => import("./pages/antenna-timeline.vue"),
        props: (route: RouteLocationNormalized) => ({
            antennaId: route.params.antennaId,
        }),
        meta: {
            auth: "normal",
        },
    },
    {
        name: "index",
        path: "",
        alias: "/",
        component: () => import("./pages/landing-page.vue"),
    },
    {
        path: "/:path(.*)*",
        component: () => import("./pages/not-found.vue"),
    },
];

export const mainRouter = createRouter({
    history: createWebHistory(),
    routes,
});

export const authGuard = (to, from, next) => {
    const accountStore = useAccountStore();
    const role = accountStore.currentUserRole;

    if (to.meta.auth === "admin" && role !== "administrator") {
        next("/");
        return undefined;
    }

    if (
        to.meta.auth === "moderator" &&
        role !== "administrator" &&
        role !== "moderator"
    ) {
        next("/");
        return undefined;
    }

    if (to.meta.auth != null && !role) {
        return openSignInModal(
            {},
            {},
            {
                message: $tr("signinRequired"),
            },
        ).catch(() => {
            return "/";
        });
    }

    next();
    return undefined;
};

mainRouter.onError(
    (
        e: any,
        to: RouteLocationNormalized,
        from: RouteLocationNormalizedLoaded,
    ) => {
        console.error(e, from, to);
    },
);

mainRouter.beforeResolve(authGuard);

mainRouter.beforeEach(
    async (
        to: RouteLocationNormalized,
        from: RouteLocationNormalizedLoaded,
    ) => {
        const devicePrefsStore = useDevicePreferencesStore();
        const deckStore = useDeckStore();
        const tileStore = useTileStore();

        if (Boolean(to.query["inPage"])) {
            return true;
        }

        if (devicePrefsStore.preferences.ui === "deck" && from.name) {
            const noMainColumn = !deckStore.currentProfile.columns.some(
                (x) => x.type === "main",
            );

            if (deckStore.currentProfile.navWindow || noMainColumn) {
                tileStore.spawnPage(to);
                return false;
            }
        }

        return undefined;
    },
);

mainRouter.afterEach((to, from, failure) => {
    globalEvents.emit("navigated", to.fullPath);
});
